import { apiClient } from "./apiClient";
import type { ChecklistConceptItem } from "../components/ImpactAssessmentChecklistForm/types";
import {
  CONCEPT_NAME_TO_DIMENSION,
  type ValuesSearchConceptMeta,
} from "./regulationsService";

/**
 * Normalized row for Process Mapping UI (GET /assessment-config/process-mappings).
 * Accepts several backend shapes: top-level array, or { processes | items | data: [...] }.
 * Each item may nest display fields under `process_template` (id, name, description, etc.).
 * Selected concepts may come from `mapping_concepts`: either `{ concepts: [{ concept, values }] }`
 * or an array of `{ concept: { name, code }, values: [{ name, code }] }`.
 */
export interface ProcessMappingRow {
  id: string;
  name: string;
  description: string;
  selectedConceptIds: string[];
  selectedConceptsDetails: ChecklistConceptItem[];
}

function extractArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const o = payload as Record<string, unknown>;
    if (Array.isArray(o.processes)) return o.processes;
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.mappings)) return o.mappings;
    const nested = o.data;
    if (Array.isArray(nested)) return nested;
    if (nested && typeof nested === "object") {
      const d = nested as Record<string, unknown>;
      if (Array.isArray(d.processes)) return d.processes;
      if (Array.isArray(d.items)) return d.items;
    }
  }
  return [];
}

function mapConceptsFromApi(raw: unknown): ChecklistConceptItem[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: ChecklistConceptItem[] = [];
  for (const x of raw) {
    if (!x || typeof x !== "object") continue;
    const o = x as Record<string, unknown>;
    const id = String(o.id ?? o.concept_id ?? "");
    if (!id) continue;
    out.push({
      id,
      name: String(o.name ?? o.label ?? id),
      dimension: String(o.dimension ?? o.category ?? "Other"),
      concept_code:
        typeof o.concept_code === "string" ? o.concept_code : undefined,
    });
  }
  return out.length ? out : undefined;
}

/**
 * Extract groups `{ concept, values[] }[]` from:
 * - `mapping_concepts.concepts`, or
 * - `mapping_concepts` when it is already that array.
 */
function extractMappingConceptGroups(raw: unknown): unknown[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    if (raw.length === 0) return [];
    const first = raw[0];
    if (
      first &&
      typeof first === "object" &&
      "concept" in first &&
      "values" in first
    ) {
      return raw;
    }
    return [];
  }
  if (typeof raw === "object") {
    const concepts = (raw as Record<string, unknown>).concepts;
    if (Array.isArray(concepts)) return concepts;
  }
  return [];
}

/**
 * Flatten API shape `{ concept: { name, code }, values: [{ name, code }] }[]`
 * into ChecklistConceptItem rows for Selected Concepts.
 */
function mapNestedMappingConceptsToItems(
  groups: unknown[],
): ChecklistConceptItem[] {
  const out: ChecklistConceptItem[] = [];
  for (const group of groups) {
    if (!group || typeof group !== "object") continue;
    const g = group as Record<string, unknown>;
    const conceptObj =
      g.concept && typeof g.concept === "object"
        ? (g.concept as Record<string, unknown>)
        : null;
    const apiCode =
      typeof conceptObj?.code === "string" ? conceptObj.code : "";
    const apiName =
      typeof conceptObj?.name === "string" ? conceptObj.name : "";
    const dimension =
      CONCEPT_NAME_TO_DIMENSION[apiName] ?? apiName ?? apiCode ?? "Other";

    const values = g.values;
    if (!Array.isArray(values)) continue;

    for (const v of values) {
      if (!v || typeof v !== "object") continue;
      const val = v as Record<string, unknown>;
      const valueCode = val.code != null ? String(val.code) : "";
      const displayName = String(val.name ?? valueCode ?? "");
      const id =
        apiCode && valueCode
          ? `${apiCode}:${valueCode}`
          : valueCode || `${dimension}:${displayName}`;
      out.push({
        id,
        name: displayName,
        dimension,
        value_api_code: valueCode || undefined,
        concept_code: apiCode || undefined,
        concept_api_code: apiCode || undefined,
        concept_api_name: apiName || undefined,
      });
    }
  }
  return out;
}

export function normalizeProcessMappingsPayload(data: unknown): ProcessMappingRow[] {
  const arr = extractArray(data);
  return arr.map((item: unknown, index: number) => {
    const row = (item && typeof item === "object" ? item : {}) as Record<
      string,
      unknown
    >;
    const rawTemplate = row.process_template ?? row["processTemplate"];
    const tmpl =
      rawTemplate && typeof rawTemplate === "object"
        ? (rawTemplate as Record<string, unknown>)
        : null;
    // Prefer id / name / description from process_template, then row-level fallbacks.
    const id = String(
      tmpl?.id ??
        tmpl?.process_id ??
        row.id ??
        row.process_id ??
        `process-${index}`,
    );
    const name = String(
      tmpl?.name ??
        tmpl?.title ??
        tmpl?.process_name ??
        row.name ??
        row.process_name ??
        "Process",
    );
    const description = String(
      tmpl?.description ??
        tmpl?.process_description ??
        row.description ??
        row.process_description ??
        "",
    );
    const idsRaw =
      row.selected_concept_ids ??
      row.concept_ids ??
      row.mapped_concept_ids ??
      row.conceptIds;

    /** Selected Concepts: `mapping_concepts.concepts` or nested `[{ concept, values }]`. */
    const mappingConceptsRaw = row.mapping_concepts ?? row["mappingConcepts"];
    const nestedGroups = extractMappingConceptGroups(mappingConceptsRaw);
    const fromNestedMapping =
      nestedGroups.length > 0
        ? mapNestedMappingConceptsToItems(nestedGroups)
        : [];
    const fromFlatMapping =
      !fromNestedMapping.length && Array.isArray(mappingConceptsRaw)
        ? mapConceptsFromApi(mappingConceptsRaw)
        : undefined;

    let selectedConceptIds: string[];
    let selectedConceptsDetails: ChecklistConceptItem[];

    if (fromNestedMapping.length > 0) {
      selectedConceptsDetails = fromNestedMapping;
      selectedConceptIds = fromNestedMapping.map((c) => c.id);
    } else if (fromFlatMapping?.length) {
      selectedConceptsDetails = fromFlatMapping;
      selectedConceptIds = fromFlatMapping.map((c) => c.id);
    } else {
      selectedConceptIds = Array.isArray(idsRaw)
        ? idsRaw.map((x) => String(x))
        : [];
      const detailsFallback =
        mapConceptsFromApi(row.mapped_concepts) ??
        mapConceptsFromApi(row.selected_concepts);
      selectedConceptsDetails = detailsFallback ?? [];
    }

    return {
      id,
      name,
      description,
      selectedConceptIds,
      selectedConceptsDetails,
    };
  });
}

export async function getProcessMappings(): Promise<ProcessMappingRow[]> {
  const res = await apiClient.get<unknown>(
    "/assessment-config/process-mappings",
  );
  const inner =
    res && typeof res === "object" && "data" in res
      ? (res as { data: unknown }).data
      : res;
  return normalizeProcessMappingsPayload(inner);
}

/** POST /assessment-config/process-mappings — one entry per process template. */
export interface ProcessMappingConceptValue {
  code: string;
  name: string;
}

export interface ProcessMappingConceptGroup {
  concept: { code: string; name: string };
  values: ProcessMappingConceptValue[];
}

export interface ProcessMappingSaveEntry {
  process_template_id: string;
  mapping_concepts: {
    concepts: ProcessMappingConceptGroup[];
  };
}

function resolveValueApiCode(item: ChecklistConceptItem): string {
  if (item.value_api_code?.trim()) return item.value_api_code.trim();
  if (item.id.includes(":")) {
    return item.id.slice(item.id.indexOf(":") + 1).trim();
  }
  return item.id.trim();
}

function resolveValuesSearchConceptMeta(
  item: ChecklistConceptItem,
  conceptMetaByKey?: Map<string, ValuesSearchConceptMeta>,
): ValuesSearchConceptMeta | null {
  // Prefer fresh values/search metadata (concept.code e.g. GCC-C000060), not stale
  // mapping_concepts from GET which may have saved display names as code.
  const lookupKeys = [
    item.dimension,
    item.concept_api_name,
    item.concept_code,
    item.concept_api_code,
  ].filter((k): k is string => Boolean(k));
  for (const key of lookupKeys) {
    const found = conceptMetaByKey?.get(key);
    if (found) return found;
  }
  if (item.concept_api_code != null && item.concept_api_name != null) {
    return { code: item.concept_api_code, name: item.concept_api_name };
  }
  return null;
}

/**
 * Build API `mapping_concepts` from checklist rows (inverse of mapNestedMappingConceptsToItems).
 * Concept `code` / `name` come from values/search (stored on items or resolved via lookup).
 */
export function buildMappingConceptsForSave(
  details: ChecklistConceptItem[],
  conceptMetaByKey?: Map<string, ValuesSearchConceptMeta>,
): {
  concepts: ProcessMappingConceptGroup[];
} {
  type Group = { meta: ValuesSearchConceptMeta; valueMap: Map<string, string> };
  const groups = new Map<string, Group>();

  for (const item of details) {
    const meta = resolveValuesSearchConceptMeta(item, conceptMetaByKey);
    if (!meta) continue;

    const valueCode = resolveValueApiCode(item);
    if (!valueCode) continue;

    const groupKey = meta.code || meta.name;
    if (!groupKey) continue;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, { meta, valueMap: new Map() });
    }
    groups.get(groupKey)!.valueMap.set(valueCode, item.name);
  }

  const concepts: ProcessMappingConceptGroup[] = Array.from(
    groups.values(),
  ).map((g) => ({
    concept: { code: g.meta.code, name: g.meta.name },
    values: Array.from(g.valueMap.entries()).map(([valCode, name]) => ({
      code: valCode,
      name,
    })),
  }));

  return { concepts };
}

/**
 * POST /assessment-config/process-mappings
 */
export async function saveProcessMappings(
  mappings: ProcessMappingSaveEntry[],
): Promise<void> {
  await apiClient.post("/assessment-config/process-mappings", {
    mappings,
  });
}
